/**
 * Earnout Scenarios Component
 *
 * Displays earnout analysis including:
 * - Best/Expected/Worst case scenarios
 * - Present value calculations
 * - Risk assessment
 */

import React from 'react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

function EarnoutScenarios({ earnoutAnalysis }) {
  if (!earnoutAnalysis) return null;

  const { basePrice, earnoutAmount, earnoutTermYears, scenarios, expected, recommendation } = earnoutAnalysis;

  const getRiskColor = (level) => {
    switch (level) {
      case 'low': return '#10b981';
      case 'medium': return '#f59e0b';
      case 'high': return '#ef4444';
      default: return '#6b7280';
    }
  };

  return (
    <div className="earnout-scenarios">
      <h2>Earnout Analysis</h2>
      <p className="section-description">
        Evaluate the potential value and risk of earnout provisions in your deal structure
      </p>

      {/* Deal Terms Summary */}
      <div className="deal-terms">
        <div className="term-card">
          <span className="term-label">Base Price at Close</span>
          <span className="term-value">{formatCurrency(basePrice)}</span>
        </div>
        <div className="term-card">
          <span className="term-label">Maximum Earnout</span>
          <span className="term-value">{formatCurrency(earnoutAmount)}</span>
        </div>
        <div className="term-card">
          <span className="term-label">Earnout Period</span>
          <span className="term-value">{earnoutTermYears} years</span>
        </div>
        <div className="term-card highlight">
          <span className="term-label">Total Deal Value</span>
          <span className="term-value">{formatCurrency(basePrice + earnoutAmount)}</span>
        </div>
      </div>

      {/* Scenario Cards */}
      <div className="scenarios-grid">
        <div className="scenario-card best">
          <div className="scenario-header">
            <h3>{scenarios.best.name}</h3>
            <span className="probability">{Math.round(scenarios.best.probability * 100)}% likely</span>
          </div>
          <div className="scenario-content">
            <div className="scenario-metric">
              <span className="label">Earnout Received</span>
              <span className="value">{formatCurrency(scenarios.best.earnoutReceived)}</span>
            </div>
            <div className="scenario-metric">
              <span className="label">Total Proceeds</span>
              <span className="value highlight">{formatCurrency(scenarios.best.totalProceeds)}</span>
            </div>
            <div className="scenario-metric">
              <span className="label">Present Value</span>
              <span className="value">{formatCurrency(scenarios.best.presentValue)}</span>
            </div>
          </div>
          <div className="scenario-bar">
            <div className="bar-fill" style={{ width: '100%', backgroundColor: '#10b981' }}></div>
          </div>
        </div>

        <div className="scenario-card expected">
          <div className="scenario-header">
            <h3>{scenarios.expected.name}</h3>
            <span className="probability">{Math.round(scenarios.expected.probability * 100)}% likely</span>
          </div>
          <div className="scenario-content">
            <div className="scenario-metric">
              <span className="label">Earnout Received</span>
              <span className="value">{formatCurrency(scenarios.expected.earnoutReceived)}</span>
            </div>
            <div className="scenario-metric">
              <span className="label">Total Proceeds</span>
              <span className="value highlight">{formatCurrency(scenarios.expected.totalProceeds)}</span>
            </div>
            <div className="scenario-metric">
              <span className="label">Present Value</span>
              <span className="value">{formatCurrency(scenarios.expected.presentValue)}</span>
            </div>
          </div>
          <div className="scenario-bar">
            <div className="bar-fill" style={{ width: '50%', backgroundColor: '#f59e0b' }}></div>
          </div>
        </div>

        <div className="scenario-card worst">
          <div className="scenario-header">
            <h3>{scenarios.worst.name}</h3>
            <span className="probability">{Math.round(scenarios.worst.probability * 100)}% likely</span>
          </div>
          <div className="scenario-content">
            <div className="scenario-metric">
              <span className="label">Earnout Received</span>
              <span className="value">{formatCurrency(scenarios.worst.earnoutReceived)}</span>
            </div>
            <div className="scenario-metric">
              <span className="label">Total Proceeds</span>
              <span className="value highlight">{formatCurrency(scenarios.worst.totalProceeds)}</span>
            </div>
            <div className="scenario-metric">
              <span className="label">Present Value</span>
              <span className="value">{formatCurrency(scenarios.worst.presentValue)}</span>
            </div>
          </div>
          <div className="scenario-bar">
            <div className="bar-fill" style={{ width: '0%', backgroundColor: '#ef4444' }}></div>
          </div>
        </div>
      </div>

      {/* Expected Value Summary */}
      <div className="expected-summary">
        <h3>Expected Value Analysis</h3>
        <div className="expected-metrics">
          <div className="metric">
            <span className="label">Expected Earnout Value</span>
            <span className="value">{formatCurrency(expected.earnoutValue)}</span>
            <span className="subtext">
              ({Math.round((expected.earnoutValue / earnoutAmount) * 100)}% of maximum)
            </span>
          </div>
          <div className="metric">
            <span className="label">Expected Total Proceeds</span>
            <span className="value">{formatCurrency(expected.totalProceeds)}</span>
          </div>
          <div className="metric">
            <span className="label">Present Value of Earnout</span>
            <span className="value">{formatCurrency(expected.presentValueOfEarnout)}</span>
            <span className="subtext">Discounted at 10%/year</span>
          </div>
          <div className="metric highlight">
            <span className="label">Effective Total Value</span>
            <span className="value">{formatCurrency(expected.effectiveTotalValue)}</span>
            <span className="subtext">Base + PV of earnout</span>
          </div>
        </div>
      </div>

      {/* Risk Assessment */}
      <div className="risk-assessment">
        <h3>Risk Assessment</h3>
        <div className="risk-indicator" style={{ borderLeftColor: getRiskColor(recommendation.riskLevel) }}>
          <div className="risk-header">
            <span className="risk-label">Risk Level</span>
            <span className={`risk-badge ${recommendation.riskLevel}`}>
              {recommendation.riskLevel.toUpperCase()}
            </span>
          </div>
          <p className="risk-guidance">{recommendation.guidance}</p>
        </div>

        {recommendation.acceptableIf && (
          <div className="acceptability-indicator positive">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <span>Earnout terms appear acceptable based on expected achievement</span>
          </div>
        )}

        {!recommendation.acceptableIf && (
          <div className="acceptability-indicator negative">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="15" y1="9" x2="9" y2="15"></line>
              <line x1="9" y1="9" x2="15" y2="15"></line>
            </svg>
            <span>Consider negotiating for higher base price or more achievable milestones</span>
          </div>
        )}
      </div>

      {/* Negotiation Tips */}
      <div className="negotiation-tips">
        <h3>Earnout Negotiation Considerations</h3>
        <ul>
          <li>
            <strong>Milestone Clarity:</strong> Ensure earnout targets are clearly defined
            with objective, measurable criteria
          </li>
          <li>
            <strong>Accounting Methods:</strong> Specify accounting standards and
            calculation methodologies in the purchase agreement
          </li>
          <li>
            <strong>Operational Control:</strong> Negotiate for sufficient control over
            business operations to achieve earnout targets
          </li>
          <li>
            <strong>Acceleration Provisions:</strong> Consider requesting earnout acceleration
            if the buyer is acquired or makes significant operational changes
          </li>
          <li>
            <strong>Dispute Resolution:</strong> Include clear dispute resolution procedures
            for earnout calculations
          </li>
        </ul>
      </div>
    </div>
  );
}

export default EarnoutScenarios;
